import { useEffect, useState } from 'react';
import { Instagram, Mail, ExternalLink } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Video {
  id: number;
  categoria: string;
  titulo: string;
  link: string;
}

export const Ayuda = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [videosByCategory, setVideosByCategory] = useState<Record<string, Video[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const { data, error } = await supabase
          .from('videos')
          .select('*')
          .order('categoria', { ascending: true });

        if (error) {
          throw error;
        }

        setVideos(data || []);
        
        // Group videos by category
        const grouped = (data || []).reduce((acc: Record<string, Video[]>, video) => {
          if (!acc[video.categoria]) {
            acc[video.categoria] = [];
          }
          acc[video.categoria].push(video);
          return acc;
        }, {});
        
        setVideosByCategory(grouped);
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // Function to get YouTube video ID from URL
  const getYouTubeVideoId = (url: string): string | null => {
    const regex = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  return (
    <div className="container mx-auto p-4 sm:p-8">
      <div className="mb-8">
        {/* Mobile title */}
        <div className="sm:hidden mb-4 text-center">
          <h1 className="text-lg font-semibold text-gray-900">Ayuda</h1>
        </div>
        {/* Desktop header */}
        <div className="hidden sm:flex items-start justify-between gap-6 mb-6">
          <div className="flex-1 text-left">
            <h1 className="text-lg font-semibold text-gray-900 mt-4">Ayuda</h1>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-6">Contacto</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Instagram */}
          <a 
            href="https://www.instagram.com/estudioatlas_" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 border rounded-lg hover:bg-blue-50 transition-colors"
          >
            <div className="bg-gradient-to-tr from-purple-500 via-pink-500 to-yellow-500 p-3 rounded-lg text-white">
              <Instagram size={24} />
            </div>
            <div>
              <h3 className="font-medium">Instagram</h3>
              <p className="text-gray-600 text-sm">@estudioatlas_</p>
            </div>
            <ExternalLink size={16} className="ml-auto text-gray-400" />
          </a>
          
          {/* WhatsApp */}
          <a 
            href="https://wa.me/5491122635392" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 border rounded-lg hover:bg-blue-50 transition-colors"
          >
            <div className="bg-[#25D366] p-3 rounded-lg text-white flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path fillRule="evenodd" clipRule="evenodd" d="M19.0781 4.9151C17.1693 2.99084 14.6008 1.93602 11.868 1.93945C6.25977 1.93945 1.70409 6.49408 1.7002 12.1084C1.69925 13.8729 2.15811 15.6037 2.99707 17.1318L1.60547 22.4004L7.00879 21.0381C8.48438 21.8008 10.1602 22.2 11.8633 22.2002H11.8691C17.4756 22.2002 22.0322 17.6445 22.0361 12.0293C22.0381 9.30713 20.9863 6.83936 19.0781 4.9151ZM11.8682 20.5254H11.8633C10.3457 20.5254 8.85645 20.1484 7.55371 19.4424L7.24609 19.2686L4.12305 20.1025L4.97266 17.0557L4.7793 16.7334C3.99023 15.375 3.57031 13.7646 3.57129 12.1094C3.57422 7.52832 7.29004 3.81543 11.873 3.81543C14.1475 3.81055 16.2803 4.6875 17.877 6.29639C19.4736 7.90527 20.3447 10.0459 20.3438 12.3281C20.3398 16.9102 16.624 20.5254 11.8682 20.5254ZM16.5898 14.2168C16.3389 14.0908 15.1201 13.4922 14.8896 13.4077C14.6592 13.3232 14.4922 13.2812 14.3242 13.5333C14.1562 13.7854 13.6816 14.3413 13.5322 14.5088C13.3828 14.6762 13.2344 14.6967 12.9834 14.5708C12.7324 14.4448 11.9302 14.1885 10.9932 13.3525C10.2666 12.7041 9.77832 11.9102 9.62891 11.6582C9.47949 11.4062 9.61426 11.2671 9.7432 11.1392C9.85742 11.0244 9.99609 10.8384 10.125 10.689C10.2539 10.5396 10.2969 10.4346 10.3809 10.2666C10.4648 10.0986 10.4219 9.94921 10.3594 9.82323C10.2969 9.69726 9.81152 8.47364 9.60254 7.96973C9.39844 7.47949 9.19141 7.54196 9.03418 7.53417C8.88477 7.52734 8.71777 7.52539 8.54980 7.52539C8.38184 7.52539 8.10742 7.58789 7.87695 7.83986C7.64648 8.09183 7.00586 8.69043 7.00586 9.91308C7.00586 11.1357 7.89844 12.3164 8.02734 12.4844C8.15625 12.6523 9.77637 15.1104 12.2227 16.1816C12.8281 16.4414 13.2998 16.5977 13.667 16.7158C14.2852 16.9102 14.8477 16.8828 15.292 16.8193C15.791 16.749 16.7783 16.2207 16.9873 15.6318C17.1963 15.043 17.1963 14.5391 17.1338 14.5088C17.0713 14.4785 16.8408 14.3428 16.5898 14.2168Z"/>
              </svg>
            </div>
            <div>
              <h3 className="font-medium">WhatsApp</h3>
              <p className="text-gray-600 text-sm">+54 9 11 2263-5392</p>
            </div>
            <ExternalLink size={16} className="ml-auto text-gray-400" />
          </a>
          
          {/* Email */}
          <a 
            href="mailto:atlas.ia.contacto@gmail.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 border rounded-lg hover:bg-blue-50 transition-colors"
          >
            <div className="bg-blue-500 p-3 rounded-lg text-white">
              <Mail size={24} />
            </div>
            <div>
              <h3 className="font-medium">Email</h3>
              <p className="text-gray-600 text-sm">atlas.ia.contacto@gmail.com</p>
            </div>
            <ExternalLink size={16} className="ml-auto text-gray-400" />
          </a>
        </div>
      </div>
      
      {(loading || videos.length > 0) && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-6">Videos de ayuda</h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(videosByCategory).map(([category, categoryVideos]) => (
                <div key={category} className="space-y-4">
                  <h3 className="text-lg font-medium border-b pb-2">{category}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categoryVideos.map((video) => {
                      const videoId = getYouTubeVideoId(video.link);
                      return (
                        <div key={video.id} className="border rounded-lg overflow-hidden bg-gray-50">
                          {videoId && (
                            <div className="aspect-w-16 aspect-h-9">
                              <iframe 
                                src={`https://www.youtube.com/embed/${videoId}`}
                                title={video.titulo}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="w-full h-full"
                              ></iframe>
                            </div>
                          )}
                          <div className="p-4">
                            <h4 className="font-medium">{video.titulo}</h4>
                            <a 
                              href={video.link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-500 text-sm flex items-center gap-1 mt-2 hover:underline"
                            >
                              Ver en YouTube <ExternalLink size={14} />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Ayuda;
